import {
  JFormik,
  getAlt,
  stringToColour,
  getContent,
  JItem,
  renderAvatarGroups,
} from '@centerpiece/components';
import {
  Typography,
  Box,
  Chip,
  Avatar as MuiAvatar,
  Paper,
} from '@mui/material';
import { useMemo, useState } from 'react';
import {
  useCreateAttachmentMutation,
  useCreateRelationshipMutation,
  useDeleteAttachmentMutation,
  useDeleteRelationshipMutation,
  useGetAttachmentsQuery,
  useGetContactsQuery,
  useGetItemStatusesQuery,
  useGetRelationshipsQuery,
  useGetSubscribersQuery,
  useLazyGetAttachmentQuery,
  usePatchAttachmentMutation,
} from 'store/api';
import {
  Attachment,
  Subscriber,
  Relationship,
  Reaction,
  Response,
  MESSAGESTATUS,
  ProjectContact,
  Contact,
} from 'store/api/models';
import { downloadFile } from 'utils/downloadFile.util';
import * as Yup from 'yup';
import { FormikHelpers } from 'formik';
import { useTagForm } from 'jformik/useTagForm';
import {
  relationshipOptions,
  useTransformRelationships,
  useAllContacts,
} from 'utils/contacts.utils';
import { useItemContext } from 'contexts/item.context';
import { useProjectContext } from 'contexts/project.context';

export type ItemInfoTabProps = {
  id: number;
  project_id: number;
  template?: boolean;
  afterSave?: Function;
};
export const ItemInfoTab = () => {
  const {
    id,
    item,
    project,
    template,
    item_type,
    patchItem,
    title,
    setTitle,
    itemLoading,
    coordination,
    event,
    task,
    refetchItem,
    setActiveTab,
    afterSave,
    submit,
    allattachments,
    messages,
    appointments,
    start_date,
    due_date,
    patchTask,
    alert: alertOnDelete,
    relationships,
    project_contacts = [],
    roles = [],
  } = useItemContext();
  const [loading, setLoading] = useState<boolean>(false);
  const { data: statuses = [] } = useGetItemStatusesQuery({});
  const [createAttachment] = useCreateAttachmentMutation();
  const [patchAttachment] = usePatchAttachmentMutation();
  const [deleteAttachment] = useDeleteAttachmentMutation();
  const [getAttachment] = useLazyGetAttachmentQuery({ refetchOnFocus: false });
  const [createRelationship] = useCreateRelationshipMutation();
  const [deleteRelationship] = useDeleteRelationshipMutation();
  const { data: subscribers = [] } = useGetSubscribersQuery({
    params: { nested: 'all' },
  });
  const responseStatuses: string[] = useMemo(() => {
    let responseStatuses: string[] = [];
    event?.e_responses?.forEach((response: Response) =>
      response.reactions.forEach((reaction: Reaction) =>
        responseStatuses.push(reaction.status)
      )
    );
    return [...new Set(responseStatuses)].sort();
  }, [event]);

  const tagForm = useTagForm(id, () => {
    refetchItem();
    afterSave();
  });
  return (
    <Paper sx={{ p: 2 }}>
      <JFormik
        autoSaveForm={true}
        enableReinitialize={true}
        initialValues={{
          ...(item?.task_r || []),
          start_date: start_date,
          due_date: due_date,
          allattachments: allattachments.filter(a => !a.task), //filter out attachment that is attached to task
          attachments: allattachments.filter(a => !!a.task),
        }}
        validationSchema={Yup.object({})}
        handleSubmit={async (values: any, formik: FormikHelpers<any>) => {
          await patchTask({
            ...values,
          } as any);
        }}
        form={[
          {
            name: 'details',
            itemType: 'textfield',
            jItemProps: { multiline: true, rows: 4, variant: 'outlined' },
            label: `Add details about your ${item_type}`,
            gridProps: { xs: 12 },
          },
          ...(template
            ? []
            : ([
                tagForm,
                {
                  name: 'relationships',
                  itemType: 'autocomplete',
                  options: relationshipOptions({
                    template,
                    roles,
                    project_contacts,
                    subscribers,
                  })(relationships),
                  multiple: true,
                  label: 'Assign',
                  loading: loading,
                  gridProps: { xs: 12 },
                  value: relationships,
                  jItemProps: {
                    disableClearable: true,
                    onChange: async (items: any[]) => {
                      if (items && Array.isArray(items) && items.length > 0) {
                        setLoading(true);
                        const newItem = items?.find(
                          (i: any) =>
                            !relationships?.find(
                              (j: any) =>
                                (i.contact === j.contact &&
                                  j.group !== 'role' &&
                                  i.group !== 'role') ||
                                (i.id === j.id &&
                                  j.group === 'role' &&
                                  i.group === 'role')
                            )
                        );
                        if (newItem) {
                          let createItem = {};
                          if (newItem.group === 'role') {
                            createItem = {
                              type: 'Assigned',
                              role: newItem.id,
                              item: id,
                            };
                          } else {
                            createItem = {
                              type: 'Assigned',
                              contact: newItem.contact,
                              item: id,
                            };
                          }
                          await createRelationship(createItem);
                          await refetchItem();
                          await afterSave();
                        }
                        setLoading(false);
                      }
                    },
                    ...renderAvatarGroups({
                      onDelete: async (
                        props: any,
                        option: any,
                        index: number,
                        getTagProps: any
                      ) => {
                        if (alertOnDelete) {
                          alertOnDelete({
                            open: true,
                            title: 'Are you sure?',
                            content: `Unassign ${
                              option.role_r
                                ? `${option.role_r?.name}${
                                    option.contact_r?.firstname
                                      ? ` (${option.contact_r?.firstname} ${option.contact_r?.lastname})`
                                      : ''
                                  } from item?${
                                    option.contact_r?.firstname
                                      ? `\n\nNOTE: If you want to change the ${option.role_r?.name} for this project, you can do this in the team tabs.`
                                      : ''
                                  }`
                                : `${option.contact_r?.firstname} ${option.contact_r?.lastname} from item?`
                            }`,
                            onConfirm: async (props: any) => {
                              setLoading(true);
                              await deleteRelationship(option.id);
                              getTagProps({ index }).onDelete(props);
                              setLoading(false);
                            },
                          });
                        } else {
                          setLoading(true);
                          await deleteRelationship(option.id);
                          getTagProps({ index }).onDelete(props);
                          setLoading(false);
                        }
                      },
                      hideEmail: true,
                    }),
                  },
                },
                {
                  precondition: () =>
                    (item?.form?.tabs?.schedule?.visible ||
                      item?.form?.tabs?.event?.visible) &&
                    appointments &&
                    appointments?.length > 0
                      ? false
                      : true,
                  name: '',
                  itemType: 'dateorrangepicker',
                  jItemProps: {
                    start_name: 'start_date',
                    end_name: 'due_date',
                    date_adornment: (
                      <small>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          DUE DATE:
                        </Typography>
                      </small>
                    ),
                    date_range_adornment: (
                      <small>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          DATE RANGE:
                        </Typography>
                      </small>
                    ),
                    start_label: 'START',
                    end_label: 'END',
                    date_label: 'DUE DATE',
                    onAccept: async (
                      value: any,
                      values: any,
                      formik: FormikHelpers<any>
                    ) => {
                      formik.setFieldValue(
                        'form.daterange.0.start',
                        value?.start
                      );
                      formik.setFieldValue('form.daterange.0.end', value?.end);
                      formik.setFieldValue('start_date', value?.start);
                      formik.setFieldValue('due_date', value?.end);
                      await patchTask({
                        id: item?.task_r?.id,
                        start_date: value?.start || null,
                        due_date: value?.end || null,
                      });
                    },
                  },
                  label: 'Item date(s) for tracking purposes',
                  // item?.form?.tabs?.schedule?.visible ||
                  // item?.form?.tabs?.event?.visible
                  //   ? 'Scheduling due date'
                  //   : 'Item date(s) for tracking purposes',
                },
                ...(
                  messages?.filter(
                    m =>
                      m.message.status !== MESSAGESTATUS.Replied &&
                      m.message.due_date
                  ) || []
                ).map((m, index) => ({
                  name: `message-${index}`,
                  itemType: 'datepicker',
                  label: 'Message response due date: ' + m.message.subject,
                  defaultValue: m.message.due_date,
                  // gridProps: { xs: 12},
                  disabled: true,
                  jItemProps: {
                    defaultValue: m.message.due_date,
                    datetimeformat: true,
                  },
                })),
                ...(appointments || []).map((a, index) => ({
                  name: `appointment-${index}`,
                  itemType: 'dateorrangepicker',
                  defaultValue: {
                    start: a.start_time,
                    end: a.end_time,
                  },
                  disabled: true,
                  jItemProps: {
                    defaultValue: {
                      start: a.start_time,
                      end: a.end_time,
                    },
                    rangeOnly: true,
                    start_name: 'start',
                    end_name: 'end',
                    date_adornment: (
                      <small>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          DUE DATE:
                        </Typography>
                      </small>
                    ),
                    date_range_adornment: (
                      <small>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          DATE RANGE:
                        </Typography>
                      </small>
                    ),
                    start_label: 'START',
                    end_label: 'END',
                    date_label: 'DUE DATE',
                  },
                  label: 'Date(s) of event: ' + (a.subject ? a.subject : ''),
                })),
                {
                  name: 'all-attachments',
                  label: 'All Attachments',
                  itemType: 'typography',
                  jItemProps: { variant: 'overline' },
                },
                {
                  precondition: (values: any) =>
                    values?.attachments?.length > 0,
                  name: 'all-attachments-bottom',
                  label: '',
                  itemType: 'divider',
                  gridProps: {
                    sx: { paddingTop: '0px !important', paddingBottom: 1 },
                  },
                },
                {
                  name: 'attachments',
                  itemType: 'dropzone',
                  label: 'Attach Documents',
                  gridProps: { xs: 12 },
                  jItemProps: {
                    onSelect: async (file: any) => {
                      const attachment = await createAttachment({
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        event: event?.id,
                        coordination: coordination?.id,
                        task: task?.id,
                        item: id,
                        project: project.id,
                      }).unwrap();
                      await fetch(attachment.url, {
                        method: 'PUT',
                        body: file,
                      });
                      await patchAttachment({
                        id: attachment.id,
                        status: 'UPLOADED',
                      } as Attachment);
                      refetchItem();
                      afterSave();
                      return attachment;
                    },
                    onDelete: async (file: any) => {
                      await deleteAttachment(file.id);
                      refetchItem();
                      afterSave();
                    },
                    onDownload: async (file: any) => {
                      const attachment: Attachment = await getAttachment(
                        file.id
                      ).unwrap();
                      await downloadFile(attachment);
                    },
                  },
                },
                {
                  precondition: (values: any) =>
                    values?.allattachments?.length > 0,
                  name: 'item-reply-attachments',
                  label: 'All Event/Reply Attachments',
                  itemType: 'typography',
                  jItemProps: { variant: 'overline' },
                },
                {
                  precondition: (values: any) =>
                    values?.allattachments?.length > 0,
                  name: 'item-reply-attachments-bottom',
                  label: '',
                  itemType: 'divider',
                  gridProps: {
                    sx: { paddingTop: '0px !important', paddingBottom: 1 },
                  },
                },
                {
                  precondition: (values: any) =>
                    values?.allattachments?.length > 0,
                  name: 'allattachments',
                  itemType: 'attachments',
                  label: '',
                  jItemProps: {
                    onDownload: async (file: any) => {
                      const attachment: Attachment = await getAttachment(
                        file.id
                      ).unwrap();
                      await downloadFile(attachment);
                    },
                  },
                },
              ] as JItem[])),
        ]}
      />
    </Paper>
  );
};
